import React, { useContext, useEffect, useState } from 'react';
import { Button, Col, Row, Table } from 'reactstrap';
import { toast } from "react-toastify";
import _ from 'lodash';
import {
   ConfirmModal, FilterDateTime, FilterText, ItemTypeahead, PODropdownMenu, POEditCostModal,
   ServiceThreadSpinner, VendorTypeahead, SortHeader
} from './';
import { DesktopContainer } from '../layouts';
import { api, constants, dateHelpers, filterHelpers, helpers, UserContext } from '../utils';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const today = new Date();
const thisYear = today.getFullYear();
const startDt = `${thisYear}-01-01`;

export default function LandedCostSearch(props) {
   const userCtx = useContext(UserContext);
   const { currentUser } = userCtx;
   const [working, setWorking] = useState(false);
   const [confirmDelete, setConfirmDelete] = useState(false);
   const [deleteId, setDeleteId] = useState(0);
   const [data, setData] = useState([]);
   const [editId, setEditId] = useState(0);
   const [filters, setFilters] = useState([]);
   const [showEditModal, setShowEditModal] = useState(false);
   const [sortField, setSortField] = useState("enteredDate");
   const [sortDirection, setSortDirection] = useState("desc");
   const [loaded, setLoaded] = useState(false);

   useEffect(() => {
      const filt = { filterName: "StartDate", value: startDt, filteredDisplay: "" };
      onFilterChange(filt);
      refreshData();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []); // initialize start date to beginning of the year

   useEffect(() => {
      if (loaded) {
         refreshData();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [sortField, sortDirection]); // Handle a table sort change

   useEffect(() => {
      if (editId > 0)
         setShowEditModal(true);
      else
         setShowEditModal(false);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [editId]); // Handle row edit request

   useEffect(() => {
      if (deleteId > 0)
         setConfirmDelete(true);
      else
         setConfirmDelete(false);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [deleteId]); // Handle row delete request

   function createPayload() {
      const payload = _.reduce(filters, (payload, filter) => {
         if (filter.filterName === "Vendor") {
            payload.Vendor = filter.value.value
         } else if (filter.filterName === "ItemNumber") {
            payload.Item = filter.value.value || ""
         } else if (filter.filterName === "StartDate") {
            payload.StartDate = filter.value
         } else if (filter.filterName === "EndDate") {
            payload.EndDate = filter.value
         } else {
            payload[filter.filterName] = filter.value;
         }
         return payload;
      }, {});

      if (payload.Vendor === undefined || payload.Vendor === null)
         payload.Vendor = "";

      if (payload.PoNum === undefined || payload.PoNum === null)
         payload.PoNum = "";

      if (payload.Container === undefined || payload.Container === null)
         payload.Container = "";

      if (payload.Item === undefined || payload.Item === null)
         payload.Item = "";

      payload.SortField = sortField;
      payload.SortDirection = sortDirection;
      return payload;
   }

   function deleteCost() {
      setConfirmDelete(false);
      setWorking(true);
      api
         .post(`Purchasing/DeleteLandedCost/${deleteId}`)
         .then(r => {
            if (r.data.success) {
               toast.success("Landed cost record successfully deleted.");
               refreshData();
            }
            else {
               toast.error(r.data.message);
               setWorking(false);
            }
         })
         .catch(api.catchHandler)
         .finally(setDeleteId(0));
   }

   const onExport = () => {
      if (!data?.length)
         return;

      const payload = createPayload();
      setWorking(true);
      api.postGetBlob("Purchasing/ExportLandedCosts", payload)
         .then(r => {
            if (r.data.success === false) {
               if (_.isString(r?.data?.message)) {
                  toast.error(r?.data?.message);
               }
               return;
            }
            if (r.data) {
               let XLSX_FILENAME = `Landed_Costs_${dateHelpers.toLongDateString(
                  dateHelpers.monthDayAndYear(Date.now())
               )}.xlsx`;
               helpers.downloadExport(r.data, XLSX_FILENAME, constants.MIME_XLSX)
            } else {
               toast.error(r.message || "Error downloading report");
            }
         }).catch(api.catchHandler)
         .finally(() => setWorking(false))
   };

   function onFilterChange(changedFilter) {
      const update = filterHelpers.getFilters(filters, changedFilter);
      setFilters(update);
      //console.log("Changed Filter: " + JSON.stringify(update));
   }

   function onSortChange(field, direction) {
      if (field !== sortField) {
         setSortField(field);
         setSortDirection("asc");
      } else {
         setSortDirection(direction);
      }
   }

   function refreshData() {
      const payload = createPayload();
      if (_.isEmpty(payload)) {
         toast.warning("Please provide at least one search parameter");
         setWorking(false); // just in case called after delete or edit of a record
         return;
      }
      setWorking(true);
      api
         .fetch('Purchasing/LandedSearch', createPayload())
         .then(r => {
            if (r.data.success) {
               setData(r.data.message);
               setLoaded(true);
            } else {
               toast.error(r.data.message);
            }
         })
         .catch(api.catchHandler)
         .finally(() => setWorking(false))
   }

   return (
      document.title = `Landed Costs`,
      <DesktopContainer screenName="Landed Costs">
         <div className="filter-header mb-4">
            <Row className="align-items-center my-1">
               <Col xs={2}>
                  <h5 className="text-white float-end">Vendor:</h5>
               </Col>
               <Col xs={6}>
                  <VendorTypeahead
                     value={filterHelpers.getValue(filters, "Vendor")}
                     onChange={opt => onFilterChange({ filterName: "Vendor", value: opt })}
                  />
               </Col>
            </Row>

            <Row className="align-items-center my-1">
               <Col xs={2} className="text-end">
                  <h5 className="d-inline text-white me-2">Item Number</h5>
               </Col>
               <Col xs={6}>
                  <ItemTypeahead
                     value={filterHelpers.getValue("ItemNumber")}
                     onChange={opt => onFilterChange({ filterName: "ItemNumber", value: opt })}
                  />
               </Col>
            </Row>

            <Row className="align-items-center my-1">
               <Col xs={2}>
                  <h5 className="text-white float-end">PO Number:</h5>
               </Col>
               <Col xs={3}>
                  <FilterText
                     filterName="PoNum"
                     value={filterHelpers.getValue(filters, "PoNum")}
                     onChangeCallback={onFilterChange}
                  />
               </Col>
               <Col xs={2}>
                  <h5 className="text-white float-end">Container:</h5>
               </Col>
               <Col xs={3}>
                  <FilterText
                     filterName="Container"
                     value={filterHelpers.getValue(filters, "Container")}
                     onChangeCallback={onFilterChange}
                  />
               </Col>
            </Row>

            <Row className="align-items-center my-1">
               <Col xs={2}>
                  <h5 className="text-white float-end">Rcpt. Start Date:</h5>
               </Col>
               <Col xs={3}>
                  <FilterDateTime
                     filterName="StartDate"
                     value={filterHelpers.getValue(filters, "StartDate")}
                     onChangeCallback={onFilterChange}
                  />
               </Col>
               <Col xs={2}>
                  <h5 className="text-white float-end">End Date:</h5>
               </Col>
               <Col xs={3}>
                  <FilterDateTime
                     filterName="EndDate"
                     value={filterHelpers.getValue(filters, "EndDate")}
                     onChangeCallback={onFilterChange}
                  />
               </Col>
            </Row>

            <Row className="mt-3">
               <Col xs={{ offset: 2, size: 4 }} className="text-center">
                  <Button className="shredButton w-100 me-4" onClick={refreshData} disabled={working} >
                     Show Landed Costs
                  </Button>
               </Col>
            </Row>
         </div>

         <ServiceThreadSpinner on={working}>
            {loaded && data?.length !== 0 ? (
               <>
                  <Row className={"mb-2"}>
                     <Col xs={{ offset: 6, size: 6 }} className="transparent-background" >
                        <Button
                           title="Download as .xlsx"
                           className="shredButtonExcel float-end ms-2"
                           onClick={onExport}
                           disabled={working}
                        >
                           <FontAwesomeIcon icon="file-excel" size="lg" />
                        </Button>
                     </Col>
                  </Row>
                  <Table size="sm" className="data-table">
                     <thead className="table-header">
                        <tr>
                           <th>
                              <SortHeader
                                 displayName={["Rcvd", <br />, "Date"]}
                                 field="enteredDate"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "enteredDate"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="Rcvd By"
                                 field="enteredBy"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "enteredBy"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="Vendor"
                                 field="vendorId"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "vendorId"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="PO"
                                 field="poNum"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "poNum"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="Container"
                                 field="container"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "container"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th className="text-end">
                              <SortHeader
                                 displayName={["Total", <br />, "LBS"]}
                                 field="qtyShipped"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "qtyShipped"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th className="text-end">
                              <SortHeader
                                 displayName={["Int'l", <br />, "Freight"]}
                                 field="freight"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "freight"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th className="text-end">
                              <SortHeader
                                 displayName={["Merch", <br />, "Fee"]}
                                 field="merchandiseFee"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "merchandiseFee"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th className="text-end">
                              <SortHeader
                                 displayName={["Harbor", <br />, "Fee"]}
                                 field="harborFee"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "harborFee"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th className="text-end">
                              <SortHeader
                                 displayName="Duty"
                                 field="duty"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "duty"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th className="text-end">
                              <SortHeader
                                 displayName={["Cash", <br />, "Advance"]}
                                 field="cashAdvanceFee"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "cashAdvanceFee"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th className="text-end">
                              <SortHeader
                                 displayName={["Courier", <br />, "Fee"]}
                                 field="courierFee"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "courierFee"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th className="text-end">
                              <SortHeader
                                 displayName={["Customs", <br />, "Fee"]}
                                 field="customsFee"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "customsFee"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th className="text-end">
                              <SortHeader
                                 displayName={["Dest", <br />, "Cartage"]}
                                 field="cartage"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "cartage"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th className="text-end">
                              <SortHeader
                                 displayName={["ISF", <br />, "Fee"]}
                                 field="securityFee"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "securityFee"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th className="text-end">
                              <SortHeader
                                 displayName="Insurance"
                                 field="insurance"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "insurance"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th className="text-end">
                              <SortHeader
                                 displayName={["Other", <br />, "Fees"]}
                                 field="lateFee"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "lateFee"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>Receipts</th>
                           <th>
                              <SortHeader
                                 displayName="Ship Method"
                                 field="shipMethod"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "shipMethod"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th className="text-end">
                              <SortHeader
                                 displayName={["TOTAL", <br />, "AMT"]}
                                 field="totalAmt"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "totalAmt"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th className="text-end">
                              <SortHeader
                                 displayName={["$ Per", <br />, "Lb/Qty"]}
                                 field="totalPerPound"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "totalPerPound"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th className="text-end">
                              <SortHeader
                                 displayName={["$ Per", <br />, "Kg"]}
                                 field="totalPerKgs"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "totalPerKgs"}
                                 callback={onSortChange}
                              />
                           </th>
                           {currentUser?.isAdmin ? (
                              <th></th>
                           ) : null}
                        </tr>
                     </thead>
                     <tbody>
                        {_.map(data, (datum, idx) => {
                           return (
                              <tr key={idx} className={`tr-${idx % 2 === 0 ? "primary" : "secondary"}`}>
                                 <td>{dateHelpers.toLongDateString(datum.enteredDate)}</td>
                                 <td>{datum.enteredBy}</td>
                                 <td>{datum.vendorId}</td>
                                 <PODropdownMenu
                                    poNumber={datum.poNum}
                                    alignTop={true}
                                    attachmentCount={datum.poAttachmentCount}
                                    showAddContainer={
                                       (currentUser?.isAdmin || currentUser?.isPowerUser || currentUser?.isPurchasingEdit) &&
                                       filterHelpers.getValue(filters, "ItemNumber")?.value?.length > 0
                                    }
                                    itemNumber={filterHelpers.getValue(filters, "ItemNumber")?.value ?? ""}
                                    containers={datum.containers}
                                    callbackRefresh={() => refreshData()}
                                 />
                                 <td>{datum.container}</td>
                                 <td className="text-end">{helpers.formatNumberWithThousands(datum.qtyShipped)}</td>
                                 <td className="text-end">{helpers.formatCurrency(datum.freight)}</td>
                                 <td className="text-end">{helpers.formatCurrency(datum.merchandiseFee)}</td>
                                 <td className="text-end">{helpers.formatCurrency(datum.harborFee)}</td>
                                 <td className="text-end">{helpers.formatCurrency(datum.duty)}</td>
                                 <td className="text-end">{helpers.formatCurrency(datum.cashAdvanceFee)}</td>
                                 <td className="text-end">{helpers.formatCurrency(datum.courierFee)}</td>
                                 <td className="text-end">{helpers.formatCurrency(datum.customsFee)}</td>
                                 <td className="text-end">{helpers.formatCurrency(datum.cartage)}</td>
                                 <td className="text-end">{helpers.formatCurrency(datum.securityFee)}</td>
                                 <td className="text-end">{helpers.formatCurrency(datum.insurance)}</td>
                                 <td className="text-end">{helpers.formatCurrency(datum.lateFee)}</td>
                                 <td><pre>{datum.receiptDisplay}</pre></td>
                                 <td>{datum.shipMethod}</td>
                                 <td className="text-end">{helpers.formatCurrency(datum.totalAmt)}</td>
                                 <td className="text-end">{helpers.formatCurrency(datum.totalPerPound)}</td>
                                 <td className="text-end">{helpers.formatCurrency(datum.totalPerKgs)}</td>
                                 {currentUser?.isAdmin ? (
                                    <td>
                                       <Button className="icon-button me-1"
                                          title="Edit"
                                          color="info"
                                          onClick={() => setEditId(datum.id)}
                                          disabled={working}
                                       >
                                          <FontAwesomeIcon className="whiteIcon" icon={"edit"} />
                                       </Button>

                                       <Button className="icon-button"
                                          title="Delete"
                                          color="danger"
                                          onClick={() => { setDeleteId(datum.id); }}
                                          disabled={working}
                                       >
                                          <FontAwesomeIcon className="whiteIcon" icon="trash" />
                                       </Button>
                                    </td>
                                 ) : null}
                              </tr>
                           )
                        })}
                     </tbody>
                     <tfoot className="table-footer">
                        <tr>
                           {currentUser?.isAdmin ? (
                              <td colSpan={23} />
                           ) : <td colSpan={22} />}
                        </tr>
                     </tfoot>
                  </Table>
               </>
            ) : (
               <div className="text-center h5">
                  <span>No activity found for the period</span>
               </div>
            )}
            {showEditModal &&
               <POEditCostModal
                  editId={editId}
                  toggleCallback={() => { setEditId(0); }}
                  callbackRefresh={() => {
                     setEditId(0);
                     refreshData();
                  }}
               />
            }
            <ConfirmModal
               confirmDelete={confirmDelete}
               approveConfirmDelete={() => deleteCost()}
               declineConfirmDelete={() => setDeleteId(0)}
               header={`Please Confirm`}
               warningMessage={
                  <>
                     Are you sure you want to delete this landed cost record?
                  </>
               }
            />
         </ServiceThreadSpinner>
      </DesktopContainer>
   );
}
